import Stack, { type StackProps } from '@liquidweb-dev/undercurrent/Stack';
import { useLeftHandLayout } from 'containers/LeftHandLayout';
import useUserMetaState from 'modules/queries/account/user/stateData/useUserMetaState';
import { useInfiniteAssetList } from 'modules/queries/asset/useList';
import { useLatestNotifications } from 'modules/queries/notification/useCurrent';
import useInfiniteScroll from 'utility/hooks/useInfiniteScroll';
import CardsWrapper from './CardsWrapper';
import EmptyFiltered from './EmptyFiltered';
import ListWrapper from './ListWrapper';
import ServerDetails from './ServerDetails';
import ServersListCard from './ServersListCard';
import ServersListRow from './ServersListRow';
import SkeletonCardItem from './SkeletonCardItem';
import SkeletonListItem from './SkeletonListItem';

export const SERVER_CATEGORIES = [
	'AutoDedicated',
	'StrictDedicated',
	'VirtualDedicated',
	'CloudSitesList',
	'CloudSitesListPlan',
	'ManagedApplication',
];

export interface ServersListProps extends StackProps {
	altGroup: string;
	project?: string;
	searchValue?: string;
	orderBySort: 'asc' | 'desc';
	onClearFilters: () => void;
}

const ServersList = ({
	altGroup,
	project,
	searchValue,
	orderBySort,
	onClearFilters,
	...rest
}: ServersListProps) => {
	const { screenSize } = useLeftHandLayout();
	const [viewPreference, , { isLoading: isLoadingUserMeta }] = useUserMetaState<
		'list' | 'card'
	>('serversViewPreference', 'list');
	const {
		data: infiniteAssetList,
		fetchNextPage,
		hasNextPage,
		isFetching,
		isFetchingNextPage,
		isLoading: isLoadingAssetList,
	} = useInfiniteAssetList(
		{
			page_size: 15,
			altGroup: altGroup || undefined,
			order_by: { field: 'domain', sort: orderBySort },
			domain_match: searchValue ? 1 : undefined,
			domain: searchValue || undefined,
			category: SERVER_CATEGORIES,
			project_name: project || undefined,
			alsowith: [
				'altGroup',
				'associationDetails',
				'backupsAndStorage',
				'capabilities',
				'categories',
				'featureDetails',
				'powerStatus',
				'region',
				'template',
				'templateApp',
			],
		},
		{ refetchInterval: 30000, keepPreviousData: true },
	);

	useInfiniteScroll({
		isFetching,
		hasNextPage,
		onShouldFetch: () => fetchNextPage(),
	});

	const { data: notifications, isLoading: isLoadingNotifications } =
		useLatestNotifications(
			{},
			{
				refetchInterval: 10000,
			},
		);

	if (isLoadingUserMeta) {
		return null;
	}

	const isLoadingServers = isLoadingAssetList || isLoadingNotifications;

	if (
		isLoadingServers &&
		(viewPreference === 'card' || screenSize === 'small')
	) {
		return (
			<Stack {...rest}>
				<CardsWrapper>
					<SkeletonCardItem />
					<SkeletonCardItem />
					<SkeletonCardItem />
				</CardsWrapper>
			</Stack>
		);
	}

	if (isLoadingServers && viewPreference === 'list') {
		return (
			<Stack {...rest}>
				<ListWrapper>
					<SkeletonListItem />
					<SkeletonListItem />
					<SkeletonListItem />
				</ListWrapper>
			</Stack>
		);
	}

	const assetList =
		infiniteAssetList?.pages?.flatMap((page) => page.items) ?? [];

	if (assetList.length === 0) {
		return (
			<Stack {...rest}>
				<EmptyFiltered
					sx={{ flexGrow: 1, alignSelf: 'center', justifyContent: 'center' }}
					onClearFilters={onClearFilters}
				/>
			</Stack>
		);
	}

	if (viewPreference === 'card' || screenSize === 'small') {
		return (
			<Stack {...rest}>
				<CardsWrapper>
					{assetList.map((assetDetails) => {
						return (
							<ServersListCard
								key={assetDetails.uniq_id}
								assetDetails={assetDetails}
								notification={notifications?.[assetDetails.uniq_id]}
							>
								<ServerDetails assetDetails={assetDetails} variant="card" />
							</ServersListCard>
						);
					})}

					{isFetchingNextPage && (
						<>
							<SkeletonCardItem />
							<SkeletonCardItem />
							<SkeletonCardItem />
						</>
					)}
				</CardsWrapper>
			</Stack>
		);
	}

	return (
		<Stack {...rest}>
			<ListWrapper>
				{assetList.map((assetDetails) => (
					<ServersListRow
						key={assetDetails.uniq_id}
						assetDetails={assetDetails}
						notification={notifications?.[assetDetails.uniq_id]}
					>
						<ServerDetails assetDetails={assetDetails} variant="row" />
					</ServersListRow>
				))}

				{isFetchingNextPage && (
					<>
						<SkeletonListItem />
						<SkeletonListItem />
						<SkeletonListItem />
						<SkeletonListItem />
					</>
				)}
			</ListWrapper>
		</Stack>
	);
};

export default ServersList;
